import React, { useEffect, useState } from "react";
import {
    Box,
    Flex,
    Text,
    Icon,
    Divider,
    Stack,
} from "@chakra-ui/react";

import {
    Calendar,
    CalendarCheck,
    Layers,
    Package,
    Star,
    ShieldCheck,
    GraduationCap,
    CircleCheck,
} from "lucide-react";

import ReusableDrawer from "components/Drawers/ReusableDrawer";
import GradientButton from "components/Buttons/GradientButton";

import StatusBadge, {
    getStatusVariant,
} from "components/Badges/StatusBadge";

import { useDispatch } from "react-redux";

import {
    updateProgressionConfigurationAction,
    fetchProgressionConfigurationsAction,
} from "state/actions/agentProgressionActions";

import { useCustomNotification } from "components/Toasts/OpenNotification";
import { formatDate } from "utils/dateFormatter";


const ViewProgressionConfiguration = ({
    isOpen,
    onClose,
    configuration,
}) => {

    const dispatch = useDispatch();

    const showToast =
        useCustomNotification();


    const [
        isEditing,
        setIsEditing,
    ] = useState(false);


    const [
        loading,
        setLoading,
    ] = useState(false);


    const [
        localConfiguration,
        setLocalConfiguration,
    ] = useState(
        configuration
    );


    const [
        formValues,
        setFormValues,
    ] = useState({
        level: "",
        minimum_experience_months: 0,
        minimum_package_volume: 0,
        minimum_customer_rating: 0,
        minimum_compliance_score: 0,
        training_completion_required: true,
        is_active: true,
    });


    useEffect(() => {

        if (configuration) {

            setLocalConfiguration(
                configuration
            );


            setFormValues({

                level:
                    configuration.level ||
                    "",

                minimum_experience_months:
                    configuration.minimum_experience_months ??
                    0,

                minimum_package_volume:
                    configuration.minimum_package_volume ??
                    0,

                minimum_customer_rating:
                    configuration.minimum_customer_rating ??
                    0,

                minimum_compliance_score:
                    configuration.minimum_compliance_score ??
                    0,

                training_completion_required:
                    configuration.training_completion_required ??
                    true,

                is_active:
                    configuration.is_active ??
                    true,

            });

        }

    }, [configuration]);


    const handleInputChange = (
        field,
        value
    ) => {

        setFormValues(
            (prev) => ({
                ...prev,
                [field]: value,
            })
        );

    };


    const handleToggleEdit =
        async () => {

            if (!isEditing) {

                setIsEditing(true);

                return;

            }


            if (
                !localConfiguration
                    ?.configuration_id
            ) {

                return;

            }


            setLoading(true);


            try {

                const payload = {

                    level:
                        formValues.level,

                    minimum_experience_months:
                        Number(
                            formValues.minimum_experience_months
                        ),

                    minimum_package_volume:
                        Number(
                            formValues.minimum_package_volume
                        ),

                    minimum_customer_rating:
                        Number(
                            formValues.minimum_customer_rating
                        ),

                    minimum_compliance_score:
                        Number(
                            formValues.minimum_compliance_score
                        ),

                    training_completion_required:
                        formValues.training_completion_required,

                    is_active:
                        formValues.is_active,

                };


                const response =
                    await dispatch(
                        updateProgressionConfigurationAction(
                            localConfiguration.configuration_id,
                            payload
                        )
                    );


                if (response?.success) {

                    setLocalConfiguration(
                        (prev) => ({
                            ...prev,
                            ...payload,
                            updated_at:
                                new Date().toISOString(),
                        })
                    );


                    showToast({

                        message:
                            "Progression configuration updated successfully!",

                        type: "success",

                        duration: 3,

                    });


                    dispatch(
                        fetchProgressionConfigurationsAction()
                    );

                } else {

                    throw new Error(
                        response?.error ||
                        "Failed to update progression configuration"
                    );

                }

            } catch (error) {

                showToast({

                    message:
                        "Failed to update progression configuration.",

                    description:
                        error.message,

                    type: "error",

                    duration: 3,

                });


                setFormValues({

                    level:
                        localConfiguration?.level ||
                        "",

                    minimum_experience_months:
                        localConfiguration?.minimum_experience_months ??
                        0,

                    minimum_package_volume:
                        localConfiguration?.minimum_package_volume ??
                        0,

                    minimum_customer_rating:
                        localConfiguration?.minimum_customer_rating ??
                        0,

                    minimum_compliance_score:
                        localConfiguration?.minimum_compliance_score ??
                        0,

                    training_completion_required:
                        localConfiguration?.training_completion_required ??
                        true,

                    is_active:
                        localConfiguration?.is_active ??
                        true,

                });

            } finally {

                setLoading(false);

                setIsEditing(false);

            }

        };


    const handleCloseDrawer =
        () => {

            setIsEditing(false);

            onClose();

        };


    if (!localConfiguration) {
        return null;
    }


    const status =
        localConfiguration.is_active
            ? "Active"
            : "Inactive";


    return (

        <Flex
            direction="column"
            p={8}
            width="100%"
        >

            <ReusableDrawer

                isOpen={isOpen}

                onClose={
                    handleCloseDrawer
                }

                headerContent={

                    <Flex
                        justifyContent="space-between"
                        alignItems="center"
                        width="100%"
                    >

                        <Text
                            fontSize="lg"
                            fontWeight="bold"
                        >
                            View Progression
                            Configuration
                        </Text>

                    </Flex>

                }


                bodyContent={

                    <Box mt={4}>

                        {/* TITLE */}

                        <Flex
                            justifyContent="space-between"
                            alignItems="center"
                        >

                            <Text
                                fontSize="2xl"
                                fontWeight="bold"
                            >
                                {localConfiguration.level}
                            </Text>

                        </Flex>


                        {/* CREATED */}

                        <Flex
                            mt={2}
                            alignItems="center"
                        >

                            <Icon
                                as={Calendar}
                                size={16}
                                color="gray.500"
                                mr={1}
                            />

                            <Text
                                fontSize="sm"
                                color="gray.500"
                                mr={4}
                            >
                                Created At:{" "}
                                {formatDate(
                                    localConfiguration.created_at
                                )}
                            </Text>

                        </Flex>


                        {/* UPDATED */}

                        <Flex
                            mt={2}
                            alignItems="center"
                        >

                            <Icon
                                as={CalendarCheck}
                                size={16}
                                color="gray.500"
                                mr={1}
                            />

                            <Text
                                fontSize="sm"
                                color="gray.500"
                                mr={4}
                            >
                                Updated At:{" "}
                                {formatDate(
                                    localConfiguration.updated_at
                                )}
                            </Text>

                        </Flex>


                        <Divider my={6} />


                        {/* DETAILS */}

                        <Flex
                            direction="column"
                            p={4}
                        >

                            <Text
                                fontSize="lg"
                                fontWeight="bold"
                                mb={2}
                            >
                                Progression
                                Requirements
                            </Text>


                            <Stack spacing={4}>

                                {/* LEVEL */}

                                <Flex
                                    alignItems="center"
                                    border="1px"
                                    borderColor="gray.300"
                                    borderRadius="md"
                                    p={3}
                                >

                                    <Icon
                                        as={Layers}
                                        mr={2}
                                        color="gray.400"
                                    />

                                    <Text
                                        fontWeight="500"
                                        color="gray.500"
                                        fontSize="sm"
                                    >
                                        Level:{" "}
                                        {localConfiguration.level}
                                    </Text>

                                </Flex>


                                {/* EXPERIENCE */}

                                <Flex
                                    alignItems="center"
                                    border="1px"
                                    borderColor="gray.300"
                                    borderRadius="md"
                                    p={3}
                                >

                                    <Icon
                                        as={Calendar}
                                        mr={2}
                                        color="gray.400"
                                    />

                                    {isEditing ? (

                                        <input
                                            type="number"
                                            value={
                                                formValues.minimum_experience_months
                                            }
                                            onChange={(e) =>
                                                handleInputChange(
                                                    "minimum_experience_months",
                                                    e.target.value
                                                )
                                            }
                                        />

                                    ) : (

                                        <Text
                                            fontWeight="500"
                                            color="gray.500"
                                            fontSize="sm"
                                        >
                                            Minimum Experience:{" "}
                                            {
                                                localConfiguration.minimum_experience_months
                                            }{" "}
                                            months
                                        </Text>

                                    )}

                                </Flex>


                                {/* PACKAGE VOLUME */}

                                <Flex
                                    alignItems="center"
                                    border="1px"
                                    borderColor="gray.300"
                                    borderRadius="md"
                                    p={3}
                                >

                                    <Icon
                                        as={Package}
                                        mr={2}
                                        color="gray.400"
                                    />

                                    {isEditing ? (

                                        <input
                                            type="number"
                                            value={
                                                formValues.minimum_package_volume
                                            }
                                            onChange={(e) =>
                                                handleInputChange(
                                                    "minimum_package_volume",
                                                    e.target.value
                                                )
                                            }
                                        />

                                    ) : (

                                        <Text
                                            fontWeight="500"
                                            color="gray.500"
                                            fontSize="sm"
                                        >
                                            Minimum Package Volume:{" "}
                                            {
                                                localConfiguration.minimum_package_volume
                                            }
                                        </Text>

                                    )}

                                </Flex>


                                {/* CUSTOMER RATING */}

                                <Flex
                                    alignItems="center"
                                    border="1px"
                                    borderColor="gray.300"
                                    borderRadius="md"
                                    p={3}
                                >

                                    <Icon
                                        as={Star}
                                        mr={2}
                                        color="gray.400"
                                    />

                                    {isEditing ? (

                                        <input
                                            type="number"
                                            min="0"
                                            max="5"
                                            step="0.1"
                                            value={
                                                formValues.minimum_customer_rating
                                            }
                                            onChange={(e) =>
                                                handleInputChange(
                                                    "minimum_customer_rating",
                                                    e.target.value
                                                )
                                            }
                                        />

                                    ) : (

                                        <Text
                                            fontWeight="500"
                                            color="gray.500"
                                            fontSize="sm"
                                        >
                                            Minimum Customer Rating:{" "}
                                            {
                                                localConfiguration.minimum_customer_rating
                                            }
                                        </Text>

                                    )}

                                </Flex>


                                {/* COMPLIANCE */}

                                <Flex
                                    alignItems="center"
                                    border="1px"
                                    borderColor="gray.300"
                                    borderRadius="md"
                                    p={3}
                                >

                                    <Icon
                                        as={ShieldCheck}
                                        mr={2}
                                        color="gray.400"
                                    />

                                    {isEditing ? (

                                        <input
                                            type="number"
                                            min="0"
                                            max="100"
                                            value={
                                                formValues.minimum_compliance_score
                                            }
                                            onChange={(e) =>
                                                handleInputChange(
                                                    "minimum_compliance_score",
                                                    e.target.value
                                                )
                                            }
                                        />

                                    ) : (

                                        <Text
                                            fontWeight="500"
                                            color="gray.500"
                                            fontSize="sm"
                                        >
                                            Minimum Compliance Score:{" "}
                                            {
                                                localConfiguration.minimum_compliance_score
                                            }
                                            %
                                        </Text>

                                    )}

                                </Flex>


                                {/* TRAINING */}

                                <Flex
                                    alignItems="center"
                                    border="1px"
                                    borderColor="gray.300"
                                    borderRadius="md"
                                    p={3}
                                >

                                    <Icon
                                        as={GraduationCap}
                                        mr={2}
                                        color="gray.400"
                                    />

                                    <Text
                                        fontWeight="500"
                                        color="gray.500"
                                        fontSize="sm"
                                    >
                                        Training Completion:{" "}

                                        <StatusBadge
                                            label={
                                                localConfiguration.training_completion_required
                                                    ? "Required"
                                                    : "Not Required"
                                            }

                                            variant={
                                                localConfiguration.training_completion_required
                                                    ? "success"
                                                    : "gray"
                                            }
                                        />

                                    </Text>

                                </Flex>


                                {/* STATUS */}

                                <Flex
                                    alignItems="center"
                                    border="1px"
                                    borderColor="gray.300"
                                    borderRadius="md"
                                    p={3}
                                >

                                    <Icon
                                        as={CircleCheck}
                                        mr={2}
                                        color="gray.400"
                                    />

                                    <Text
                                        fontWeight="500"
                                        color="gray.500"
                                        fontSize="sm"
                                    >
                                        Status:{" "}

                                        <StatusBadge
                                            label={status}
                                            variant={
                                                getStatusVariant(
                                                    status
                                                )
                                            }
                                        />

                                    </Text>

                                </Flex>

                            </Stack>


                            {/* EDIT BUTTON */}

                            <Flex
                                justifyContent="flex-end"
                                mt={6}
                            >

                                <GradientButton

                                    height="40px"

                                    fontSize="sm"

                                    onClick={
                                        handleToggleEdit
                                    }

                                    isLoading={
                                        loading
                                    }

                                    size="lg"
                                >
                                    {
                                        isEditing
                                            ? "Save"
                                            : "Edit"
                                    }
                                </GradientButton>

                            </Flex>

                        </Flex>

                    </Box>

                }

            />

        </Flex>

    );

};


export default ViewProgressionConfiguration;